import { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Shield,
  Clock,
  FileText,
  CheckCircle,
  DollarSign,
  AlertTriangle,
  XCircle,
  CreditCard,
  Mail,
  Phone
} from "lucide-react";

const Refund = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const today = new Date();
  const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        {/* Header */}
        <header className="pt-20 pb-12 px-4">
          <div className="max-w-6xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl mb-6 shadow-xl">
              <DollarSign className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Refund and Cancellation Policy
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our refund and cancellation policy for vehicle wash bookings and services.
            </p>
            <Badge variant="secondary" className="mt-4 bg-green-100 text-green-800 border-green-200 px-4 py-2 text-sm font-medium">
              <Clock className="w-4 h-4 mr-2" />
              Effective Date: {formattedDate}
            </Badge>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 py-12 space-y-8">
          {/* Introduction */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-3xl font-bold text-gray-900 flex items-center">
                <FileText className="w-8 h-8 text-green-600 mr-3" />
                Policy Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed mb-4">
                Timelyyy Technologies ("Company") charges and collects in advance for the use of our vehicle wash booking services.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                All services rendered are non-refundable. All monthly, quarterly, yearly, and multi-year subscriptions renew automatically on their due renewal date according to the date of purchase until officially cancelled in writing.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Customers may cancel anytime by emailing a notice to support@timelyyy.com.
              </p>
            </CardContent>
          </Card>

          {/* No Refunds Policy */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                <XCircle className="w-6 h-6 text-red-600 mr-3" />
                No Refunds Policy
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                  <h3 className="font-semibold text-red-800">Important Notice</h3>
                </div>
                <p className="text-red-700 leading-relaxed">
                  No refunds or credits for partial months or years of service will be refunded to a customer upon cancellation.
                </p>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                Due to the nature of our service and the resources required to maintain our platform, we do not offer refunds for:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Completed vehicle wash bookings</li>
                <li>Partial service periods</li>
                <li>Subscription cancellations</li>
                <li>Service interruptions due to circumstances beyond our control</li>
                <li>User-initiated cancellations</li>
              </ul>
            </CardContent>
          </Card>

          {/* Cancellation Policy */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                <CheckCircle className="w-6 h-6 text-blue-600 mr-3" />
                Cancellation Policy
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed mb-4">
                You may cancel your subscription at any time by contacting our support team. However, please note that:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 mb-6">
                <li>Cancellations must be made in writing via email to support@timelyyy.com</li>
                <li>Cancellations take effect at the end of your current billing period</li>
                <li>You will continue to have access to the service until the end of your billing period</li>
                <li>No refunds will be provided for the remaining period</li>
              </ul>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="font-semibold text-blue-800 mb-2">How to Cancel</h3>
                <p className="text-blue-700 text-sm">
                  Send an email to support@timelyyy.com with your account details and cancellation request. We will process your cancellation within 24-48 hours.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Payment Terms */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                <CreditCard className="w-6 h-6 text-green-600 mr-3" />
                Payment Terms
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed mb-4">
                In case the amount remains outstanding for more than fifteen (15) days from the date of activation of services (for first-time users) & more than one (1) day for auto-renewal cases, the company reserves the right to cancel the subscription.
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                We accept various payment methods including:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Credit Cards (Visa, MasterCard, American Express)</li>
                <li>Debit Cards</li>
                <li>Digital Wallets (PayPal, Google Pay, Apple Pay)</li>
                <li>Bank Transfers</li>
                <li>UPI Payments</li>
              </ul>
            </CardContent>
          </Card>

          {/* Service Interruptions */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                <Shield className="w-6 h-6 text-orange-600 mr-3" />
                Service Interruptions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed mb-4">
                In the event of service interruptions due to technical issues, maintenance, or circumstances beyond our control:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>We will make every effort to restore service as quickly as possible</li>
                <li>No refunds will be provided for service interruptions</li>
                <li>We will notify users of any planned maintenance in advance</li>
                <li>Emergency maintenance may be performed without prior notice</li>
              </ul>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm p-8 animate-fade-in">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                <Mail className="w-6 h-6 text-blue-600 mr-3" />
                Contact Us
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed mb-6">
                If you have any questions about our refund and cancellation policy, please contact us:
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="font-semibold text-gray-900">Email Support</p>
                    <p className="text-sm text-gray-600">support@timelyyy.com</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                  <Phone className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-semibold text-gray-900">Phone Support</p>
                    <p className="text-sm text-gray-600">+91 98765 43210</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </main>

      </div>
  );
};

export default Refund;
